import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, STATIC_BASE_URL } from './config';
import { BUILDING_NAME_MAPPING, MANUAL_COORDINATES } from './constants';
import VideoIntro from './components/VideoIntro';
import CampusMap from './components/CampusMap';
import BuildingDetail from './components/BuildingDetail';
import { useIsMobile } from './hooks/useIsMobile';
import './App.css';

function App() {
  const isMobile = useIsMobile();

  // Mobile detection and routing
  if (isMobile) {
    // Dynamically import MobileApp to reduce bundle size
    const MobileApp = React.lazy(() => import('./mobile/MobileApp'));
    
    return (
      <Router>
        <React.Suspense fallback={
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            fontSize: '18px'
          }}>
            Mobil uygulama yükleniyor...
          </div>
        }>
          <Routes>
            <Route path="/*" element={<MobileApp />} />
          </Routes>
        </React.Suspense>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<VideoIntroPage />} />
        <Route path="/building/:buildingName" element={<BuildingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Video Intro Page Component
function VideoIntroPage() {
  const [showVideo, setShowVideo] = useState(true);
  const [videoStartAtEnd, setVideoStartAtEnd] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedSearchResult, setSelectedSearchResult] = useState(null);

  // Manuel coordinates - API'den yüklenmezse bunları kullan
  const manualCoordinates = MANUAL_COORDINATES;

  // Load data when video ends
  useEffect(() => {
    if (!showVideo) {
      console.log('Video bitti, veriler yükleniyor...');
      loadData();
    }
  }, [showVideo]);

  // Preload campus map image immediately
  useEffect(() => {
    const preloadImage = new Image();
    preloadImage.src = `${STATIC_BASE_URL}/static/images/campus_map_small.jpg`;
    preloadImage.onload = () => {
      console.log('Campus map image preloaded successfully');
    };
    preloadImage.onerror = () => {
      console.error('Failed to preload campus map image');
      // Fallback to a different image if the main one fails
      console.log('Trying fallback image...');
    };
  }, []);

  // Preload when video starts
  useEffect(() => {
    if (showVideo) {
      const preloadImage = new Image();
      preloadImage.src = `${STATIC_BASE_URL}/static/images/campus_map_small.jpg`;
    }
  }, [showVideo]);

  // Debug için coordinates'ları kontrol et
  useEffect(() => {
    console.log('Coordinates güncellendi:', coordinates);
    console.log('Manuel coordinates:', manualCoordinates);
  }, [coordinates]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // API'den koordinat yükleme - sadece manuel koordinatları kullan
      console.log('App - Manuel koordinatlar kullanılıyor');
      setCoordinates([]); // API koordinatlarını temizle
      
      // Load buildings
      const buildingsResponse = await axios.get(`${API_BASE_URL}/buildings`);
      const buildings = buildingsResponse.data.buildings || [];
      console.log('App - Loaded buildings:', buildings);
      setBuildings(buildings);
      
    } catch (err) {
      setError('Veri yüklenirken hata oluştu');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoEnd = () => {
    setShowVideo(false);
    setVideoStartAtEnd(false); // Video bittiğinde state'i sıfırla
  };

  // URL encoding fonksiyonu - % yerine _ kullan
  const encodeBuildingName = (name) => {
    return name.replace(/ /g, '_').replace(/[^a-zA-Z0-9_çğıöşüÇĞIÖŞÜ]/g, '_');
  };

  const handleBuildingClick = async (buildingName) => {
    try {
      console.log('Bina tıklama:', buildingName);
      
      const buildingNameForAPI = BUILDING_NAME_MAPPING[buildingName];
      console.log('API için bina adı:', buildingNameForAPI);
      
      if (buildingNameForAPI) {
        const response = await axios.get(`${API_BASE_URL}/buildings/${encodeURIComponent(buildingNameForAPI)}`);
        console.log('Bina detayları:', response.data);
        // Video sayfasından geliyorsa, video sayfasını kapat
        if (showVideo) {
          setShowVideo(false);
        }
        // Navigate to building page
        const encodedName = encodeBuildingName(buildingNameForAPI);
        window.location.href = `/building/${encodedName}`;
      } else {
        console.error('Bina eşleştirmesi bulunamadı:', buildingName);
      }
    } catch (err) {
      console.error('Error loading building details:', err);
    }
  };

  const handleBackToVideo = () => {
    setVideoStartAtEnd(true); // Video'nun son karesinde başla
    setShowVideo(true);
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      console.log('Arama yapılıyor:', query);
      const response = await axios.get(`${API_BASE_URL}/rooms`);
      const allBuildings = response.data.buildings || [];
      console.log('Backend\'den gelen veri:', allBuildings);
      
      const results = allBuildings.filter((building) => 
        building.floors.some((floor) => 
          floor.rooms.some((room) => 
            room.room_name.toLowerCase().includes(query.toLowerCase()) ||
            room.room_number.toLowerCase().includes(query.toLowerCase())
          )
        )
      ).map((building) => {
        const matchingRooms = building.floors.map((floor) => ({
          floor_name: floor.floor_name,
          rooms: floor.rooms.filter((room) => 
            room.room_name.toLowerCase().includes(query.toLowerCase()) ||
            room.room_number.toLowerCase().includes(query.toLowerCase())
          )
        })).filter((floor) => floor.rooms.length > 0);

        return {
          building_name: building.building_name,
          matching_rooms: matchingRooms
        };
      }).filter((building) => building.matching_rooms.length > 0);

      console.log('Arama sonuçları:', results);
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Arama hatası:', error);
      setSearchResults([]);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleSearchResultClick = (buildingName, roomData) => {
    console.log('Arama sonucu tıklama:', buildingName, roomData);
    
    if (buildingName === '' && roomData === null) {
      // Çarpıya basıldığında arama sonuçlarını tekrar göster
      setSelectedSearchResult(null);
      setShowSearchResults(true);
    } else {
      // Oda seçildiğinde arama sonuçlarını kapat
      setShowSearchResults(false);
      
      // Seçilen oda bilgilerini state'e kaydet
      setSelectedSearchResult({
        buildingName: buildingName,
        roomData: roomData
      });
    }
  };

  const handleViewBuildingDetails = async (buildingName, selectedFloor) => {
    try {
      console.log('Bina detayları görüntüleniyor:', buildingName, 'Seçilen kat:', selectedFloor);
      
      const buildingNameForAPI = buildingName;
      console.log('API için bina adı:', buildingNameForAPI);
      
      // Navigate to building page
      const encodedName = encodeBuildingName(buildingNameForAPI);
      window.location.href = `/building/${encodedName}`;
    } catch (err) {
      console.error('Error loading building details:', err);
    }
  };

  // Show video intro first (Desktop only)
  if (showVideo) {
    console.log('App - Rendering VideoIntro with coordinates:', coordinates);
    
    const coordsToUse = manualCoordinates; // Her zaman manuel koordinatları kullan
    
    return <VideoIntro 
      onVideoEnd={handleVideoEnd} 
      onBuildingClick={handleBuildingClick}
      coordinates={coordsToUse}
      startAtEnd={videoStartAtEnd} // Geri dönüşte video'nun son karesinde başla
      searchQuery={searchQuery}
      onSearchInputChange={handleSearchInputChange}
      searchResults={searchResults}
      showSearchResults={showSearchResults}
      onSearchResultClick={handleSearchResultClick}
      selectedSearchResult={selectedSearchResult}
      onViewBuildingDetails={handleViewBuildingDetails}
    />;
  }

  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <h2>Yükleniyor...</h2>
          <p>Kampüs haritası yükleniyor</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Hata</h2>
          <p>{error}</p>
          <button onClick={loadData}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  // Navigate to campus page
  return <CampusPage />;
}

// Campus Page Component
function CampusPage() {
  const [coordinates, setCoordinates] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // URL encoding fonksiyonu - % yerine _ kullan
  const encodeBuildingName = (name) => {
    return name.replace(/ /g, '_').replace(/[^a-zA-Z0-9_çğıöşüÇĞIÖŞÜ]/g, '_');
  };

  // Manuel coordinates - API'den yüklenmezse bunları kullan
  const manualCoordinates = MANUAL_COORDINATES;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // API'den koordinat yükleme - sadece manuel koordinatları kullan
      console.log('App - Manuel koordinatlar kullanılıyor');
      setCoordinates([]); // API koordinatlarını temizle
      
      // Load buildings
      const buildingsResponse = await axios.get(`${API_BASE_URL}/buildings`);
      const buildings = buildingsResponse.data.buildings || [];
      console.log('App - Loaded buildings:', buildings);
      setBuildings(buildings);
      
    } catch (err) {
      setError('Veri yüklenirken hata oluştu');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingClick = async (buildingName) => {
    try {
      console.log('Bina tıklama:', buildingName);
      
      const buildingNameForAPI = BUILDING_NAME_MAPPING[buildingName];
      console.log('API için bina adı:', buildingNameForAPI);
      
      if (buildingNameForAPI) {
        // Navigate to building page
        const encodedName = encodeBuildingName(buildingNameForAPI);
        window.location.href = `/building/${encodedName}`;
      } else {
        console.error('Bina eşleştirmesi bulunamadı:', buildingName);
      }
    } catch (err) {
      console.error('Error loading building details:', err);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <h2>Yükleniyor...</h2>
          <p>Kampüs haritası yükleniyor</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Hata</h2>
          <p>{error}</p>
          <button onClick={loadData}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <main>
        <CampusMap 
          coordinates={manualCoordinates}
          buildings={buildings}
          onBuildingClick={handleBuildingClick}
        />
      </main>
    </div>
  );
}

// Building Page Component
function BuildingPage() {
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // URL encoding fonksiyonu - % yerine _ kullan
  const encodeBuildingName = (name) => {
    return name.replace(/ /g, '_').replace(/[^a-zA-Z0-9_çğıöşüÇĞIÖŞÜ]/g, '_');
  };

  useEffect(() => {
    const loadBuilding = async () => {
      try {
        setLoading(true);
        const buildingName = window.location.pathname.split('/building/')[1].replace(/_/g, ' '); // _ karakterlerini boşlukla değiştir
        console.log('Loading building:', buildingName);
        
        const response = await axios.get(`${API_BASE_URL}/buildings/${buildingName}`);
        console.log('Building details:', response.data);
        setBuilding(response.data);
      } catch (err) {
        setError('Bina detayları yüklenirken hata oluştu');
        console.error('Error loading building details:', err);
      } finally {
        setLoading(false);
      }
    };

    loadBuilding();
  }, []);

  const handleBackToCampus = () => {
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <h2>Yükleniyor...</h2>
          <p>Bina detayları yükleniyor</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Hata</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  if (!building) {
    return (
      <div className="App">
        <div className="error">
          <h2>Bina Bulunamadı</h2>
          <p>İstediğiniz bina bulunamadı.</p>
          <button onClick={() => window.location.href = '/'}>Ana Sayfaya Dön</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <main>
        <BuildingDetail 
          building={building} 
          onBack={handleBackToCampus}
          apiBaseUrl={API_BASE_URL}
        />
      </main>
    </div>
  );
}

export default App;
